import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Hand,
  Stethoscope,
  UtensilsCrossed,
  Users,
  Calendar,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Heart,
  Building2,
  Quote,
  ClipboardEdit,
  UserCheck,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";
import { PageShell } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Reveal, CountUp } from "@project/user/components/reveal";
import { useI18n } from "@project/lib/use-i18n";
import {
  GALLERY_BY_CATEGORY,
  HOME_GLIMPSE_CATEGORIES,
} from "@project/lib/gallery-images";
import { UserTestimonialsCarousel } from "@project/user/components/user-testimonials-carousel";
import { VisitorRegisterChooserDialog } from "@project/user/components/visitor-register-chooser-dialog";
import Marquee from "react-fast-marquee";

const schemes = [
  {
    icon: Users,
    to: "/elder-care-program",
    titleKey: "home.schemes.residentCareTitle",
    descKey: "home.schemes.residentCareDesc",
    buttonKey: "home.schemes.residentCareButton",
  },
  {
    icon: Building2,
    to: "/gaushala",
    titleKey: "home.schemes.gaushalaTitle",
    descKey: "home.schemes.gaushalaDesc",
    buttonKey: "home.schemes.gaushalaButton",
  },
  {
    icon: UtensilsCrossed,
    to: "/tiffin-seva",
    titleKey: "home.schemes.tiffinTitle",
    descKey: "home.schemes.tiffinDesc",
    buttonKey: "home.schemes.tiffinButton",
  },
  {
    icon: Stethoscope,
    to: "/healthcare",
    titleKey: "home.schemes.healthcareTitle",
    descKey: "home.schemes.healthcareDesc",
    buttonKey: "home.schemes.healthcareButton",
  },
];

const homeGlimpseCards = [
  {
    labelKey: "home.glimpses.festivalLabel",
    subKey: "home.glimpses.festivalSub",
  },
  {
    labelKey: "home.glimpses.gaushalaLabel",
    subKey: "home.glimpses.gaushalaSub",
  },
  {
    labelKey: "home.glimpses.derasarLabel",
    subKey: "home.glimpses.derasarSub",
  },
] as const;

export default function HomePage() {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [visitorChooserOpen, setVisitorChooserOpen] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    if (v.paused) {
      void v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    const next = !v.muted;
    v.muted = next;
    if (!next) {
      void v.play();
    }
    setIsMuted(next);
  };

  return (
    <PageShell>
      <VisitorRegisterChooserDialog
        open={visitorChooserOpen}
        onOpenChange={setVisitorChooserOpen}
      />
      {/* ============ HERO (full-bleed video background) ============ */}
      <section className="relative isolate overflow-hidden bg-black">
        {/* Background video */}
        <video
          ref={videoRef}
          className="absolute inset-0 top-[0%] z-0 w-full object-cover object-center md:top-[-8%]"
          src="https://universalsoftwarefiles.s3.ap-south-1.amazonaws.com/JivanDhara.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          style={{ height: "100vh" }}
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-black/60 via-black/60 to-black/40" />
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />

        <div className="relative z-10 flex min-h-[100svh] w-full items-end px-5 pb-16 pt-24 sm:px-6 md:min-h-[88svh] md:px-12 md:pb-16 md:pt-32 lg:min-h-[91.5svh] lg:px-16">
          {/* Bottom bar: name and admission button on the right */}
          <div className="flex w-full justify-end">
            <div className="max-w-2xl md:text-right">
              <Reveal>
                <div className="border-gold/40 bg-gold/15 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold text-gold backdrop-blur-sm">
                  <span className="text-base leading-none">🙏</span>
                  <span className="tracking-wider">
                    {t("home.hero.greeting")}
                  </span>
                  <Sparkles className="h-3 w-3 text-gold" />
                </div>
              </Reveal>
              <Reveal delay={120}>
                <h1 className="mt-6 font-serif leading-[1.02] tracking-tight drop-shadow-lg">
                  <span className="block font-serif text-2xl text-gray-300 sm:text-3xl md:text-4xl">
                    {t("home.hero.titleLine1")}
                  </span>
                  <span className="mt-2 block text-4xl font-semibold italic text-gold sm:text-5xl md:text-5xl">
                    {t("home.hero.titleLine2")}
                  </span>
                </h1>
                <p className="mt-3 font-serif text-base italic text-gray-300 drop-shadow">
                  {t("home.hero.sanskrit")}
                </p>
              </Reveal>
              <Reveal delay={420}>
                <div className="mt-6 flex md:justify-end">
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-primary/30 rounded-full bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  >
                    <Link to="/admission-inquiry">
                      <ClipboardEdit className="mr-2 h-4 w-4" />{" "}
                      {t("home.hero.admissionInquiry")}
                    </Link>
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Video controls (reference style) */}
        <div className="absolute bottom-5 left-8 z-30 flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause video" : "Play video"}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/40 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/25"
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="ml-0.5 h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute video" : "Mute video"}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/40 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/25"
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </button>
        </div>
      </section>

      {/* ============ Trust strip (4 stats) ============ */}
      <section className="border-border/60 border-t bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:py-16">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground">
            <span className="text-saffron">❋</span> {t("home.trust.heading")}{" "}
            <span className="text-saffron">❋</span>
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Building2,
                n: 120,
                suffix: "+",
                label: t("home.trust.residentsCared"),
              },
              {
                icon: Users,
                n: 1000,
                suffix: "+",
                label: t("home.trust.generousDonors"),
              },
              {
                icon: Heart,
                n: 2,
                suffix: "Cr+",
                label: t("home.trust.donationsProcessed"),
              },
              {
                icon: ShieldCheck,
                n: 15,
                suffix: "+ Yrs",
                label: t("home.trust.ofTrustedSeva"),
              },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 90}>
                <div className="card-lift rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
                  <div className="bg-primary/10 mx-auto grid h-12 w-12 place-items-center rounded-xl text-primary">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4 text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
                    <CountUp to={s.n} suffix={s.suffix} />
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {s.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ Schemes ============ */}
      <section className="bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <Reveal className="text-center">
            <div className="ornament mb-4" aria-hidden>
              <span className="ornament-dot" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              {t("home.schemes.eyebrow")}
            </p>
            <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
              {t("home.schemes.heading")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              {t("home.schemes.description")}
            </p>
          </Reveal>

          <div className="mt-10 overflow-hidden">
            <Marquee pauseOnHover speed={40} gradient={false} className="py-2">
              {[0, 1].flatMap(pass =>
                schemes.map(s => (
                  <Link
                    key={`${pass}-${s.to}`}
                    to={s.to}
                    className="card-lift border-gold/30 group mx-2.5 flex h-[300px] w-[280px] shrink-0 flex-col overflow-hidden rounded-2xl border bg-card p-6 shadow-sm transition-all duration-300 hover:border-gold sm:mx-3 sm:h-[320px] sm:w-[300px]"
                  >
                    <div className="bg-primary/10 grid h-12 w-12 shrink-0 place-items-center rounded-xl text-primary transition-transform group-hover:scale-110">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 line-clamp-2 min-h-[3.5rem] shrink-0 font-serif text-lg font-bold leading-snug text-foreground">
                      {t(s.titleKey)}
                    </h3>
                    <p className="mt-2 line-clamp-4 min-h-0 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {t(s.descKey)}
                    </p>
                    <div className="mt-auto flex shrink-0 items-center justify-between border-t border-border pt-4">
                      <span className="text-sm font-bold text-primary">
                        {t(s.buttonKey)}
                      </span>
                      <span className="bg-primary/10 grid h-8 w-8 shrink-0 place-items-center rounded-full text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                ))
              )}
            </Marquee>
          </div>
        </div>
      </section>

      {/* ============ Admission + Visitors CTA ============ */}
      <section className="border-border/60 from-primary/5 to-gold/10 border-t bg-gradient-to-br via-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:py-16">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
              {/* Admission Enquiry — left */}
              <Reveal className="card-lift border-gold/40 hover:shadow-gold/15 group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-card p-5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg sm:p-6 lg:p-7">
                <div className="border-gold/40 bg-gold/10 inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold">
                  <ClipboardEdit className="h-3.5 w-3.5" />{" "}
                  {t("home.admission.badge")}
                </div>
                <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                  {t("home.admission.heading")}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("home.admission.description")}
                </p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  {[
                    t("home.admission.feature1"),
                    t("home.admission.feature2"),
                    t("home.admission.feature3"),
                    t("home.admission.feature4"),
                  ].map(f => (
                    <div
                      key={f}
                      className="text-foreground/80 flex items-center gap-2 text-sm"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />{" "}
                      {f}
                    </div>
                  ))}
                </div>
                <div className="border-primary/15 bg-primary/[0.03] mt-5 grid overflow-hidden rounded-2xl border sm:grid-cols-[7fr_3fr]">
                  <div className="border-border/70 p-4 text-left sm:border-r">
                    <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {t("home.admission.howItWorks")}
                    </div>
                    <ol className="text-foreground/80 mt-2 space-y-1.5 text-[13px] leading-4">
                      <li>{t("home.admission.step1")}</li>
                      <li>{t("home.admission.step2")}</li>
                      <li>{t("home.admission.step3")}</li>
                      <li>{t("home.admission.step4")}</li>
                    </ol>
                  </div>
                  <div className="bg-secondary/40 flex flex-col items-center justify-center border-t border-border px-3 py-5 text-center sm:border-t-0">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                      <ClipboardEdit className="h-5 w-5" />
                    </div>
                    <div className="mt-2 text-2xl font-extrabold text-foreground">
                      <CountUp to={120} suffix="+" />
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t("home.admission.eldersAdmitted")}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="shadow-primary/20 hover:bg-primary/90 group rounded-full bg-primary text-primary-foreground shadow-md"
                  >
                    <Link to="/admission-inquiry">
                      <ClipboardEdit className="mr-2 h-4 w-4" />{" "}
                      {t("home.admission.applyButton")}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <Link to="/contact">{t("home.admission.talkButton")}</Link>
                  </Button>
                </div>
              </Reveal>

              {/* Visitor Registration — right */}
              <Reveal
                delay={120}
                className="card-lift border-gold/40 hover:shadow-gold/15 group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-card p-5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg sm:p-6 lg:p-7"
              >
                <div className="border-gold/40 bg-gold/10 inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gold">
                  <UserCheck className="h-3.5 w-3.5" />{" "}
                  {t("home.visitors.badge")}
                </div>
                <h2 className="mt-4 font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                  {t("home.visitors.heading")}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t("home.visitors.description")}
                </p>
                <div className="mt-5 grid gap-2 sm:grid-cols-2">
                  {[
                    t("home.visitors.feature1"),
                    t("home.visitors.feature2"),
                    t("home.visitors.feature3"),
                    t("home.visitors.feature4"),
                  ].map(f => (
                    <div
                      key={f}
                      className="text-foreground/80 flex items-center gap-2 text-sm"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />{" "}
                      {f}
                    </div>
                  ))}
                </div>
                <div className="border-primary/15 bg-primary/[0.03] mt-5 grid overflow-hidden rounded-2xl border sm:grid-cols-[7fr_3fr]">
                  <div className="border-border/70 p-4 text-left sm:border-r">
                    <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {t("home.visitors.howItWorks")}
                    </div>
                    <ol className="text-foreground/80 mt-2 space-y-1.5 text-[13px] leading-4">
                      <li>{t("home.visitors.step1")}</li>
                      <li>{t("home.visitors.step2")}</li>
                      <li>{t("home.visitors.step3")}</li>
                      <li>{t("home.visitors.step4")}</li>
                    </ol>
                  </div>
                  <div className="bg-secondary/40 flex flex-col items-center justify-center border-t border-border px-3 py-5 text-center sm:border-t-0">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-md">
                      <UserCheck className="h-5 w-5" />
                    </div>
                    <div className="mt-2 text-2xl font-extrabold text-foreground">
                      <CountUp to={500} suffix="+" />
                    </div>
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      {t("home.visitors.visitsLogged")}
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="shadow-primary/20 hover:bg-primary/90 group rounded-full bg-primary text-primary-foreground shadow-md"
                    onClick={() => setVisitorChooserOpen(true)}
                  >
                    <UserCheck className="mr-2 h-4 w-4" />{" "}
                    {t("home.visitors.applyButton")}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    <Link to="/contact">{t("home.visitors.talkButton")}</Link>
                  </Button>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ============ Glimpses gallery ============ */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <Reveal className="text-center">
          <div className="ornament mb-4" aria-hidden>
            <span className="ornament-dot" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            {t("home.glimpses.eyebrow")}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            {t("home.glimpses.heading")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            {t("home.glimpses.description")}
          </p>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {HOME_GLIMPSE_CATEGORIES.map((categoryId, i) => {
            const image = GALLERY_BY_CATEGORY[categoryId][0];
            const card = homeGlimpseCards[i];

            if (!image || !card) {
              return null;
            }

            const label = t(card.labelKey);
            const sub = t(card.subKey);

            return (
              <Reveal key={categoryId} variant="zoom" delay={i * 110}>
                <figure className="card-lift group relative overflow-hidden rounded-2xl border border-border shadow-sm">
                  <img
                    src={image.src}
                    alt={label}
                    loading="lazy"
                    decoding="async"
                    width={1024}
                    height={1024}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="pointer-events-none absolute inset-0" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-4 text-primary-foreground">
                    <div className="text-base font-bold">{label}</div>
                    <div className="text-primary-foreground/85 text-xs">
                      {sub}
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-8 text-center">
          <Button
            asChild
            variant="outline"
            className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link to="/gallery">
              {t("home.glimpses.viewGallery")}{" "}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <UserTestimonialsCarousel />

      {/* ============ Mission band ============ */}
      <section className="bg-secondary/40">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-3">
          <Pillar
            i={0}
            icon={Users}
            title={t("home.pillars.residentCareTitle")}
            body={t("home.pillars.residentCareBody")}
          />
          <Pillar
            i={1}
            icon={Stethoscope}
            title={t("home.pillars.medicalTitle")}
            body={t("home.pillars.medicalBody")}
          />
          <Pillar
            i={2}
            icon={Calendar}
            title={t("home.pillars.ritualsTitle")}
            body={t("home.pillars.ritualsBody")}
          />
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center md:py-20">
          <Reveal>
            <Quote className="mx-auto h-7 w-7 text-gold" />
            <h2 className="mt-4 font-serif text-3xl font-semibold text-foreground md:text-5xl">
              {t("home.cta.headingBefore")}{" "}
              <span className="italic text-gold">
                {t("home.cta.headingHighlight")}
              </span>
              {t("home.cta.headingAfter")}
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              {t("home.cta.description")}
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="hover:bg-gold/90 rounded-full bg-gold text-gold-foreground"
              >
                <Link to="/donate">
                  <Hand className="mr-2 h-4 w-4" />
                  {t("home.cta.donateNow")}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-primary/30 rounded-full text-primary hover:bg-primary hover:text-primary-foreground"
              >
                <Link to="/contact">{t("home.cta.visitUs")}</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ Shloka band ============ */}
      <section className="bg-mandala border-gold/30 -mb-20 border-y">
        <div className="mx-auto max-w-4xl px-4 py-14 text-center">
          <div className="ornament mb-5" aria-hidden>
            <span className="ornament-dot" />
          </div>
          <p className="font-serif text-2xl italic text-primary md:text-3xl">
            {t("home.shloka.sanskrit")}
          </p>
          <p className="mt-2 text-sm font-medium tracking-wide text-muted-foreground">
            {t("home.shloka.translation")}
          </p>
          <div className="ornament mt-5" aria-hidden>
            <span className="ornament-dot" />
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Pillar({
  i,
  icon: Icon,
  title,
  body,
}: {
  i: number;
  icon: typeof Users;
  title: string;
  body: string;
}) {
  return (
    <Reveal delay={i * 140} className="text-center md:text-left">
      <div className="group">
        <div className="shadow-primary/20 mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-md transition-transform group-hover:scale-110 md:mx-0">
          <Icon className="h-7 w-7" />
        </div>
        <h3 className="mt-5 text-xl font-bold text-foreground">{title}</h3>
        <p className="mt-2 text-muted-foreground">{body}</p>
      </div>
    </Reveal>
  );
}
